import { useEffect, useState } from "react";
import { Workflow, Save, Loader2, CheckCircle2, Copy } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getWebhookSetting, setWebhookSetting } from "@/api";

export const WebhookConfigDialog = () => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [configured, setConfigured] = useState(false);
  const [schema, setSchema] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const data = await getWebhookSetting();
      setUrl(data.n8n_webhook_url || "");
      setConfigured(data.configured);
      setSchema(data.payload_schema);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await setWebhookSetting(url);
      setConfigured(res.configured);
      toast.success(res.configured ? "Webhook de n8n guardado" : "Webhook eliminado");
    } catch {
      toast.error("No se pudo guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const copySchema = () => {
    navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
    toast.success("Esquema JSON copiado");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" data-testid="open-webhook-config-button" className="rounded-full text-slate-500 hover:text-brand-navy">
          <Workflow className="w-4 h-4" /> Integraciones
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-brand-navy flex items-center gap-2">
            <Workflow className="w-5 h-5 text-brand-teal" /> Automatización con n8n
          </DialogTitle>
          <DialogDescription>
            Al pulsar "Enviar al paciente", NotaViva enviará un POST a tu Webhook de n8n.
            Desde ahí puedes reenviar a WhatsApp, email o tu CRM.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="webhook-url" className="text-brand-navy">URL del Webhook de n8n</Label>
              {configured ? (
                <Badge className="bg-brand-teal/10 text-brand-teal border-0"><CheckCircle2 className="w-3 h-3 mr-1" /> Configurado</Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-500 border-0">Sin configurar</Badge>
              )}
            </div>
            <Input
              id="webhook-url"
              data-testid="webhook-url-input"
              placeholder="https://n8n.tu-vps.com/webhook/notaviva"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="h-11 font-mono text-sm"
            />
            <p className="text-xs text-slate-400 mt-1">Déjalo vacío para mantener el envío en modo simulado.</p>
          </div>

          <Button
            data-testid="save-webhook-button"
            onClick={save}
            disabled={saving}
            className="w-full rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white h-11"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar configuración
          </Button>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-brand-navy">JSON que recibirá n8n</Label>
              <Button variant="ghost" size="sm" data-testid="copy-schema-button" onClick={copySchema} className="text-slate-500 h-8">
                <Copy className="w-3.5 h-3.5" /> Copiar
              </Button>
            </div>
            <pre className="bg-brand-navy text-teal-100 text-xs rounded-xl p-4 overflow-x-auto max-h-64" data-testid="webhook-schema">
{schema ? JSON.stringify(schema, null, 2) : "…"}
            </pre>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
